import { useMemo } from 'react'
import { last, unique } from 'lib/array/arrayUtils'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'

export default function useFlightPurchaseDetails(order: App.Order) {
  const {
    status,
    flightItems,
  } = order

  const [firstFlight, fetchingFirst] = useOrderFlightDetails(order.id, order.flightItems[0].id)
  const [lastFlight, fetchingLast] = useOrderFlightDetails(order.id, order.flightItems.length > 1 ? last(order.flightItems).id : undefined)
  const fetching = order.flightItems.length > 1 ? fetchingFirst && fetchingLast : fetchingFirst

  const legs = useMemo(() => {
    if (firstFlight && lastFlight) {
      return [...firstFlight.bookingJourney.legs, ...lastFlight.bookingJourney.legs]
    } else if (firstFlight) {
      return firstFlight.bookingJourney.legs
    }
    return []
  }, [firstFlight, lastFlight])

  const tripType = useMemo(() => {
    if (legs.length > 2) {
      return 'MultiCity'
    }
    if (legs.length === 2) {
      const isOpenJaw = last(legs[0].flights).arrivalAirport !== legs[1].flights[0].departureAirport
      const isReverseOpenJaw = last(legs[1].flights).arrivalAirport !== legs[0].flights[0].departureAirport
      if (isOpenJaw || isReverseOpenJaw) {
        // Open jaw flights are considered to be Multi-City
        return 'MultiCity'
      }
    }

    if (firstFlight && lastFlight) {
      return 'TwoOneWay'
    }

    if (legs.length === 2 && firstFlight && !lastFlight) {
      return 'Return'
    }

    if (legs.length === 1) {
      return 'OneWay'
    }

    return ''
  }, [legs, firstFlight, lastFlight])

  // firstly try to use a collection of leg PNRs
  // - These have to be deduped as flights with multiple legs from the same airline have the same PNR
  let pnrIds = Array.from(new Set(legs.map(leg => leg.pnr).filter(pnr => !!pnr)))

  // fallback to using the pnrId on the flightItems
  if (!pnrIds || pnrIds.length === 0) {
    pnrIds = flightItems.map(flightItem => flightItem.pnrId)
  }

  const noOfAdults = useMemo(() => legs[0]?.paxCount.adult, [legs])
  const noOfChildren = useMemo(() => legs[0]?.paxCount.child, [legs])
  const noOfInfants = useMemo(() => legs[0]?.paxCount.infant, [legs])
  const departingFlight = legs[0]?.flights[0]
  const arrivalFlight = tripType === 'OneWay' ? last(legs[0]?.flights) : last(legs)?.flights[0]
  const arrivalFlightLabel = tripType === 'OneWay' ? arrivalFlight?.arrivalAirportName + ', ' + arrivalFlight?.arrivalCountry : arrivalFlight?.departureAirportName + ', ' + arrivalFlight?.departureCountry
  const departingDate = departingFlight?.departingDate
  const arrivalDate = arrivalFlight?.arrivalDate ?? departingDate

  const carrierLogos = useMemo(() => {
    const urls = unique(legs.map(leg => leg.flights[0].marketingAirline.logoUrl))

    if (urls.length > 1) {
      return unique(legs.map(leg => leg.flights[0].marketingAirline.brandMarkUrl))
    }

    return urls
  }, [legs])

  const flightsName = useMemo(() => {
    if (tripType === 'MultiCity') {
      const uniqueCities = new Set()
      legs.forEach(leg => {
        uniqueCities.add(leg.flights[0].departureCity)
        uniqueCities.add(last(leg.flights).arrivalCity)
      })
      const cities: Array<string> = Array.from(uniqueCities) as Array<string>
      return `Flights to ${cities.slice(0, -1).join(', ')} & ${cities.slice(-1)}`
    }

    // Return, TwoOneWays and OneWay flights
    return departingFlight && arrivalFlight && `${departingFlight.departureAirportName}, ${departingFlight.departureCountry} to ${arrivalFlightLabel}`
  }, [tripType, departingFlight, arrivalFlight, arrivalFlightLabel, legs])

  const flightTypeLabel = useMemo(() => {
    if (tripType === 'MultiCity') {
      return 'Multi-city flights'
    }

    return tripType === 'OneWay' ? 'One-way Flight' : 'Return Flights'
  }, [tripType])

  return {
    fetching,
    flightsName,
    departingDate,
    arrivalDate,
    noOfAdults,
    noOfChildren,
    noOfInfants,
    departingFlight,
    arrivalFlight,
    arrivalFlightLabel,
    flightTypeLabel,
    carrierLogos,
    pnrIds,
    status,
  }
}
